import React, { useCallback, useEffect, useState } from 'react';
import {
    getProjectProgress, getStatusBreakdown,
    type Project, type ProjectProgress, type StatusCount,
} from '../../../packages/api-client/pm-api';
import { exportProjectCSV } from '../../../packages/api-client/export-api';
import { BurndownChart } from '../../../components/BurndownChart';
import { ChartFigure } from '../../../components/ChartFigure';
import { StateView, Table } from '../../../packages/ui';
import { statusColor } from '../../../utils/statusColors';
import { Download, Printer, TriangleAlert } from 'lucide-react';
import { MetricStrip } from './dashboardParts';

const label = (s: string) => s.replace(/_/g, ' ');

/**
 * Reports › Analytics: completion, tasks by status and the burndown. Every
 * number comes from the project's scoped endpoints (progress, status-breakdown,
 * burndown); a failed request shows an error, never zeros.
 */
export function AnalyticsSection({ project }: { project: Project }) {
    const [data, setData] = useState<{ progress: ProjectProgress; breakdown: StatusCount[] } | null>(null);
    const [error, setError] = useState(false);

    const load = useCallback(() => {
        setError(false); setData(null);
        Promise.all([getProjectProgress(project.id), getStatusBreakdown(project.id)])
            .then(([progress, breakdown]) => setData({ progress, breakdown }))
            .catch(() => setError(true));
    }, [project.id]);
    useEffect(load, [load]);

    return (
        <div className="rpt-analytics">
            <ReportsToolbar project={project} />
            {error ? (
                <StateView state="error" title="Analytics couldn't be loaded" message="The progress or status request failed." onRetry={load} />
            ) : !data ? (
                <StateView state="loading" label="Loading analytics" />
            ) : (
                <>
                    <MetricStrip breakdown={data.breakdown} full />
                    <div className="rpt-grid">
                        <CompletionCard progress={data.progress} color={project.color} />
                        <section className="pd-panel pd-panel--block" aria-labelledby="rpt-status-h">
                            <span id="rpt-status-h" className="pd-eyebrow">Status breakdown</span>
                            <StatusBreakdown breakdown={data.breakdown} />
                        </section>
                    </div>
                </>
            )}
            <section className="pd-panel pd-panel--block" style={{ marginTop: 'var(--space-5)' }} aria-label="Burndown">
                <BurndownChart projectId={project.id} />
            </section>
        </div>
    );
}

/** CSV export (GET /projects/{id}/export, scoped server-side) and print. */
function ReportsToolbar({ project }: { project: Project }) {
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const exportCsv = () => {
        setBusy(true); setFailed(false);
        exportProjectCSV(project.id, project.name)
            .catch(() => setFailed(true))
            .finally(() => setBusy(false));
    };
    return (
        <div className="rpt-toolbar no-print">
            <p className="rpt-muted">Measured from the tasks you can see in this project.</p>
            <div className="rpt-toolbar-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={exportCsv} disabled={busy} aria-busy={busy || undefined}>
                    <Download size={14} aria-hidden="true" /> {busy ? 'Exporting…' : 'Export tasks CSV'}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.print()}>
                    <Printer size={14} aria-hidden="true" /> Print
                </button>
            </div>
            {failed && <p role="alert" className="rpt-muted" style={{ color: 'var(--color-danger)', width: '100%' }}>The CSV export failed. Nothing was downloaded.</p>}
        </div>
    );
}

function CompletionCard({ progress, color }: { progress: ProjectProgress; color?: string }) {
    const pct = Math.round(progress.percent ?? 0);
    return (
        <section className="pd-panel pd-panel--block" aria-labelledby="rpt-completion-h">
            <span className="pd-eyebrow">Completion</span>
            <h3 id="rpt-completion-h">Completion {pct}%</h3>
            <p>{progress.done_tasks} of {progress.total_tasks} tasks done</p>
            <div className="pd-track" role="progressbar" aria-label="Project completion" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                <span style={{ width: `${pct}%`, background: color || undefined }} />
            </div>
            {progress.overdue_tasks > 0 && (
                <p style={{ color: 'var(--danger-text)', marginTop: 'var(--space-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <TriangleAlert size={14} aria-hidden="true" /> {progress.overdue_tasks} overdue
                </p>
            )}
        </section>
    );
}

function StatusBreakdown({ breakdown }: { breakdown: StatusCount[] }) {
    const total = breakdown.reduce((s, x) => s + x.count, 0);
    const share = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
    if (total === 0) return <StateView state="empty" title="No tasks yet" message="Status counts appear once the project has tasks." />;
    const rows = breakdown.map(s => ({ status: label(s.status), count: s.count, share: `${share(s.count)}%` }));
    return (
        <ChartFigure title="Tasks by status"
            summary={`Tasks by status: ${rows.map(r => `${r.status} ${r.count}`).join(', ')}.`}
            chart={breakdown.map(s => (
                <div key={s.status} style={{ marginBottom: 'var(--space-3)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 'var(--font-size-sm)' }}>
                        <span style={{ textTransform: 'capitalize' }}>{label(s.status)}</span>
                        <span className="rpt-muted">{s.count} ({share(s.count)}%)</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, background: 'var(--color-border)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 4, width: `${share(s.count)}%`, background: statusColor(s.status) }} />
                    </div>
                </div>
            ))}
            table={<Table caption="Tasks by status" density="compact" rowKey={r => String(r.status)}
                columns={[{ key: 'status', header: 'Status', rowHeader: true }, { key: 'count', header: 'Tasks' }, { key: 'share', header: 'Share' }]}
                data={rows} />}
        />
    );
}
